<template>
  <div class="room-tree-container">
    <el-tree ref="tree" :data="treeData" node-key="id" :props="defaultProps" :highlight-current="true"
      :expand-on-click-node="false" @node-click="handleNodeClick">
      <span slot-scope="{ node, data }" class="custom-tree-node">
        <span>
          <i :class="data.icon" style="margin-right: 5px"></i>
          {{ node.label }}
        </span>
      </span>
    </el-tree>
  </div>
</template>

<script>
import { queryUnits, queryRoomsTree } from '@/api/fee/meterWaterManageApi'
import { getCommunityId } from '@/api/community/communityApi'

export default {
  name: 'RoomTreeDiv',
  props: {
    showRoomLevel: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      treeData: [],
      defaultProps: {
        children: 'children',
        label: 'text'
      },
      communityId: '',
      lastSelected: {},
      isInitialized: false
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.loadTreeData()
  },
  methods: {
    async loadTreeData() {
      let lastSelected = localStorage.getItem('lastSelectedRoom')
      if (lastSelected) {
        try {
          this.lastSelected = JSON.parse(lastSelected)
        } catch (error) {
          console.error('解析lastSelected失败:', error)
          localStorage.removeItem('lastSelectedRoom')
        }
      }
      
      try {
        const units = await queryUnits({
          communityId: this.communityId
        })
        this.buildTreeData(units)
      } catch (error) {
        console.error('Failed to load tree data:', error)
        this.$message.error(this.$t('roomTree.loadError'))
      }
    },
    buildTreeData(units) {
      const floorMap = {}

      units.forEach(unit => {
        if (!floorMap[unit.floorId]) {
          floorMap[unit.floorId] = {
            id: `f_${unit.floorId}`,
            floorId: unit.floorId,
            floorNum: unit.floorNum,
            icon: '/img/floor.png',
            text: `${unit.floorNum}${this.$t('room.floorUnitTree.building')}`,
            children: []
          }
        }

        floorMap[unit.floorId].children.push({
          id: `u_${unit.unitId}`,
          unitId: unit.unitId,
          unitNum: unit.unitNum,
          floorId: unit.floorId,
          floorNum: unit.floorNum,
          icon: '/img/unit.png',
          text: `${unit.unitNum}${this.$t('room.floorUnitTree.unit')}`,
          children: []
        })
      })

      this.treeData = Object.values(floorMap)
      this.$nextTick(() => {
        if (this.showRoomLevel && this.lastSelected && this.lastSelected.floorId && this.lastSelected.unitId) {
          this.expandAndSelectLastRoom()
        } else {
          this.expandAndSelectFirstItem()
        }
      })
    },
    async expandAndSelectLastRoom() {
      if (!this.lastSelected || !this.lastSelected.floorId || !this.lastSelected.unitId) {
        this.expandAndSelectFirstItem()
        return
      }

      const treeRef = this.$refs.tree
      if (!treeRef || !treeRef.store) {
        setTimeout(() => this.expandAndSelectLastRoom(), 100)
        return
      }

      try {
        const floorNode = this.treeData.find(floor => floor.floorId === this.lastSelected.floorId)
        if (!floorNode) {
          this.expandAndSelectFirstItem()
          return
        }

        const unitNode = floorNode.children.find(unit => unit.unitId === this.lastSelected.unitId)
        if (!unitNode) {
          this.expandAndSelectFirstItem()
          return
        }

        const floorTreeNode = treeRef.store.nodesMap[floorNode.id]
        const unitTreeNode = treeRef.store.nodesMap[unitNode.id]
        
        if (floorTreeNode) {
          floorTreeNode.expanded = true
        }
        
        if (unitTreeNode) {
          unitTreeNode.expanded = true
        }

        if (this.showRoomLevel) {
          await this.loadRooms(unitNode, { data: unitNode })
          
          if (this.lastSelected.roomId) {
            this.selectSpecificRoom(this.lastSelected.roomId)
          }
        } else {
          this.$emit('selectRoom', {
            floorId: floorNode.floorId,
            unitId: unitNode.unitId,
            floorNum: floorNode.floorNum,
            unitNum: unitNode.unitNum,
            roomName: `${floorNode.floorNum}-${unitNode.unitNum}`
          })
        }

        this.isInitialized = true
      } catch (error) {
        console.error('展开上次选择的房屋失败:', error)
        this.expandAndSelectFirstItem()
      }
    },
    selectSpecificRoom(roomId) {
      const treeRef = this.$refs.tree
      if (!treeRef) return

      const roomNodeId = `r_${roomId}`
      const roomTreeNode = treeRef.store.nodesMap[roomNodeId]
      
      if (roomTreeNode) {
        treeRef.setCurrentKey(roomNodeId)
        
        this.$emit('selectRoom', {
          roomId: roomId,
          roomName: this.lastSelected.roomName || ''
        })
      } else {
        setTimeout(() => this.selectSpecificRoom(roomId), 200)
      }
    },
    async expandAndSelectFirstItem() {
      if (!this.treeData.length) return
      
      const firstFloor = this.treeData[0]
      if (!firstFloor.children || !firstFloor.children.length) return
      const firstUnit = firstFloor.children[0]
      const treeRef = this.$refs.tree
      
      if (treeRef && treeRef.store) {
        treeRef.store.nodesMap[firstFloor.id] && (treeRef.store.nodesMap[firstFloor.id].expanded = true)
      }
      
      if (this.showRoomLevel) {
        if (treeRef && treeRef.store) {
          treeRef.store.nodesMap[firstUnit.id] && (treeRef.store.nodesMap[firstUnit.id].expanded = true)
        }
        await this.loadRooms(firstUnit, { data: firstUnit })
      } else {
        this.$emit('selectRoom', {
          floorId: firstFloor.floorId,
          unitId: firstUnit.unitId,
          floorNum: firstFloor.floorNum,
          unitNum: firstUnit.unitNum,
          roomName: `${firstFloor.floorNum}-${firstUnit.unitNum}`
        })
      }
      
      this.isInitialized = true
    },
    async handleNodeClick(data, node) {
      if (data.id.startsWith('f_')) {
        this.$emit('selectRoom', {
          floorId: data.floorId,
          floorNum: data.floorNum,
          roomName: data.text
        })
      } else if (data.id.startsWith('u_')) {
        if (this.showRoomLevel && !node.expanded) {
          await this.loadRooms(data, node)
          node.expanded = true
        }
        this.$emit('selectRoom', {
          floorId: data.floorId,
          unitId: data.unitId,
          floorNum: data.floorNum,
          unitNum: data.unitNum,
          roomName: `${data.floorNum}-${data.unitNum}`
        })
      } else if (data.id.startsWith('r_')) {
        const parentNodes = this.getParentNodes(data)
        let selectedData = {
          floorId: parentNodes.floorId,
          unitId: parentNodes.unitId,
          roomId: data.roomId,
          roomName: data.roomName
        }
        
        localStorage.setItem('lastSelectedRoom', JSON.stringify(selectedData))
        this.lastSelected = selectedData

        this.$emit('selectRoom', {
          roomId: data.roomId,
          roomName: data.roomName,
          floorId: parentNodes.floorId,
          unitId: parentNodes.unitId
        })
      }
    },
    getParentNodes(data) {
      const treeRef = this.$refs.tree
      if (!treeRef || !treeRef.store) return { floorId: '', unitId: '', floorNum: '', unitNum: '' }

      const node = treeRef.store.nodesMap[data.id]
      if (!node || !node.parent) return { floorId: '', unitId: '', floorNum: '', unitNum: '' }

      const unitNode = node.parent
      const floorNode = unitNode.parent

      return {
        floorId: floorNode ? floorNode.data.floorId : '',
        unitId: unitNode ? unitNode.data.unitId : '',
        floorNum: floorNode ? floorNode.data.floorNum : '',
        unitNum: unitNode ? unitNode.data.unitNum : ''
      }
    },
    async loadRooms(unitData, node) {
      try {
        const { rooms } = await queryRoomsTree({
          unitId: unitData.unitId,
          communityId: this.communityId,
          page: 1,
          row: 1000
        })

        if (rooms && rooms.length > 0) {
          const roomNodes = rooms.map(room => ({
            id: `r_${room.roomId}`,
            roomId: room.roomId,
            roomName: `${room.floorNum}-${room.unitNum}-${room.roomNum}`,
            icon: '/img/room.png',
            text: room.ownerName
              ? `${room.roomNum}(${room.ownerName})`
              : `${room.roomNum}`
          }))

          this.$set(node.data, 'children', roomNodes)
          
          if (!this.isInitialized) {
            this.$emit('selectRoom', {
              roomId: roomNodes[0].roomId,
              roomName: roomNodes[0].roomName
            })
          }
        }
      } catch (error) {
        console.error('Failed to load rooms:', error)
        this.$message.error(this.$t('roomTree.loadRoomError'))
      }
    },
    clearLastSelected() {
      localStorage.removeItem('lastSelectedRoom')
      this.lastSelected = {}
    }
  }
}
</script>

<style lang="scss" scoped>
.room-tree-container {
  height: 100%;
  overflow-y: auto;
  padding: 10px;

  .custom-tree-node {
    flex: 1;
    display: flex;
    align-items: center;
    font-size: 14px;
    padding: 5px 0;
  }
}
</style>
